import { getBranchId } from "../utils/branch";
import React, { useEffect, useState } from "react";
import { Button, Card, Col, Modal, Row, Spin, Table, Tooltip, message } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { Input } from "antd";
import { SearchOutlined, CarOutlined, CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import styled from "styled-components";
import { useSelector, useDispatch } from "react-redux";
import ace from "../assets/ace2.png";
import bolero from "../assets/bolero2.png";
import eicher from "../assets/Eicher2.png";
import tata407 from "../assets/tata4072.png";
import three from "../assets/three.png";
import { getAvailableCabsRadiusRequest } from "../redux/actions/newAction";

type MessageApi = ReturnType<typeof message.useMessage>[0];

const vehicleImageMap: Record<string, string> = {
  "ace": ace,
  "bolero": bolero,
  "eicher": eicher,
  "tata 407": tata407,
  "three wheeler": three,
};

const { Search } = Input;

interface AssignFeatureProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRowKey: any | null;
  selectedBookingData?: any;
  assignLoading?: boolean;
  onAssignConfirm?: (rowKey: any, vehicleData: any) => void;
}

interface AssignVehicleType {
  key: string;
  cabId: string;
  driverid: string;
  distance: string;
  vehicle: string;
  drivername: string;
  permit: string;
  location: string;
  trips: number;
  amount: string;
  minutes: number;
  length?: string;
  height?: string;
  width?: string;
  bodyType?: string;
  startlocation?: string;
}

const StyledTable = styled(Table as React.FC<TableProps<any>>)`
  .ant-table-thead > tr > th {
    background: #884299 !important;
    color: white !important;
    font-weight: 600;
    text-align: center;
    height: 60px;
  }

  .ant-table-thead > tr > th::before {
    display: none !important;
  }

  /* .ant-table-tbody > tr:hover > td {
    background: #faf5ff;
  } */

  .ant-table-tbody > tr {
    height: 60px;
  }
`;

type AssignTableProps = TableProps<AssignVehicleType>;

const AssignVehicleTable: React.FC<AssignTableProps> = (props) => {
  return <StyledTable {...props} />;
};

const Assignmodel: React.FC<AssignFeatureProps> = ({
  open,
  setOpen,
  selectedRowKey,
  selectedBookingData,
  assignLoading,
  onAssignConfirm,
}) => {
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedVehicleRowKey, setSelectedVehicleRowKey] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");

  const dispatch = useDispatch();
  const {
    availableCabsRadius,
    availableCabsRadiusLoading,
    selectedCab,
  } = useSelector((state: any) => state.newBooking || {});

  const { locations } = useSelector((state: any) => state.booking || {});

  const selectedBooking = selectedBookingData || {};

  const resolvePickupCoordinates = () => {
    if (
      selectedBooking.fromloclat &&
      selectedBooking.fromloclong
    ) {
      return {
        fromlat: String(selectedBooking.fromloclat),
        fromlong: String(selectedBooking.fromloclong),
      };
    }

    if (
      selectedBooking.fromlat &&
      selectedBooking.fromlong &&
      !isNaN(Number(selectedBooking.fromlat)) &&
      !isNaN(Number(selectedBooking.fromlong))
    ) {
      return {
        fromlat: String(selectedBooking.fromlat),
        fromlong: String(selectedBooking.fromlong),
      };
    }

    const locationMatch = (locations || []).find((loc: any) =>
      String(loc.PlaceName || "").trim().toLowerCase() ===
      String(selectedBooking.fromloc || "").trim().toLowerCase()
    );

    if (
      locationMatch?.Latitude &&
      locationMatch?.Longitude
    ) {
      return {
        fromlat: String(locationMatch.Latitude),
        fromlong: String(locationMatch.Longitude),
      };
    }

    return {
      fromlat: "",
      fromlong: "",
    };
  };

  const [tick, setTick] = useState(0);

  const getMinutesSinceCompletion = (lastcompletedtime?: string): number => {
    if (!lastcompletedtime) return 0;
    const completed = new Date(lastcompletedtime).getTime();
    const now = Date.now();
    if (isNaN(completed)) return 0;
    const diffMs = now - completed;
    return diffMs > 0 ? Math.floor(diffMs / 60000) : 0;
  };

  const allCabs =
    selectedCab
      ? [selectedCab]
      : availableCabsRadius || [];

  const data: AssignVehicleType[] = allCabs.map((cab: any, index: number) => ({
    ...cab,

    key: cab.cabid || index.toString(),
    cabId: cab.cabid,
    driverid: cab.driverid,
    distance: cab.distance,

    vehicle: cab.cabtype || cab.vehicle || cab.vehicletype || "-",
    bodyType: cab.bodytype || cab.bodyType || "-",
    drivername: cab.drivername || cab.driver || "Unknown",
    permit: cab.permit || "yes",
    location: cab.location || cab.place || cab.landmark || "-",

    trips: cab.totaltrip ?? cab.trips ?? 0,
    amount: cab.tripfare ?? cab.amount ?? "₹0",

    minutes: getMinutesSinceCompletion(cab.lastcompletedtime),

    length: cab.length || "",
    width: cab.width || "",
    height: cab.height || "",
  }));

  const filteredData = data.filter((item) => {
    if (!searchText) return true;
    const lowerSearch = searchText.toLowerCase();
    return (
      (item.cabId && item.cabId.toLowerCase().includes(lowerSearch)) ||
      (item.drivername && item.drivername.toLowerCase().includes(lowerSearch)) ||
      (item.vehicle && item.vehicle.toLowerCase().includes(lowerSearch))
    );
  });

  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(interval);
  }, [open]);

  useEffect(() => {
    if (selectedCab?.cabid) {
      //console.log("Using searched cab:", selectedCab);

      setSelectedVehicleRowKey(selectedCab.cabid);
    }
  }, [selectedCab]);

  const selectedVehicle = data.find(item => item.key === selectedVehicleRowKey);

  React.useEffect(() => {
    if (!open) return;

    dispatch({
      type: "GET_AVAILABLE_CABS_RADIUS_RESET",
    });

    if (selectedCab?.cabid) {
      //console.log("Using searched cab:", selectedCab);
      return;
    }

    const branchid =
      selectedBooking.cityid || selectedBooking.branchid || getBranchId();

    const vehicletype = String(
      selectedBooking.vehicleid ||
      selectedBooking.vehicletypeid ||
      1
    );

    const { fromlat, fromlong } = resolvePickupCoordinates();

    if (!fromlat || !fromlong) {
      messageApi.warning("Pickup location coordinates are unavailable for the selected booking.");
      return;
    }
    //console.log("requesting vehicletype:", vehicletype, "from booking:", selectedBooking.vehicleid, selectedBooking.vehicletypeid);
    dispatch(
      getAvailableCabsRadiusRequest(
        branchid,
        Number(vehicletype),
        fromlat,
        fromlong
      )
    );
  }, [
    open,
    dispatch,
    selectedCab,
    selectedBooking.id,
    selectedBooking.cityid,
    selectedBooking.branchid,
    selectedBooking.vehicleid,
    selectedBooking.vehicletypeid,
    selectedBooking.fromloclat,
    selectedBooking.fromloclong,
    selectedBooking.fromlat,
    selectedBooking.fromlong,
    selectedBooking.fromloc,
    locations,
  ]);

  const handleAssignClick = () => {
    if (!selectedRowKey) {
      messageApi.warning("Please select a row first");
      return;
    }
    setOpen(true);
  };

  const handleAssignConfirm = () => {
    if (!selectedRowKey || !onAssignConfirm) return;

    if (!selectedVehicle) {
      messageApi.warning("Please select a vehicle");
      return;
    }

    onAssignConfirm(selectedRowKey, selectedVehicle);
  };

  const isWithinHomeWindow = (): boolean => {
    const now = new Date();
    const hours = now.getHours();
    return hours >= 18 && hours < 24;
  };

  const getAssignColumns = (
    selectedVehicleRowKey: string | null,
    handleAssignConfirm: () => void,
    messageApi: MessageApi
  ): ColumnsType<AssignVehicleType> => {
    const columns: ColumnsType<AssignVehicleType> = [
      {
        title: "Cab ID",
        dataIndex: "cabId",
        width: 100,
        align: "center",
        render: (text: string, record: AssignVehicleType) => {
          const isSelected = selectedVehicleRowKey === record.key;
          return (
            <span
              onClick={() => {
                if (isSelected) {
                  handleAssignConfirm();
                } else {
                  messageApi.warning("Please select the radio button first to assign this vehicle");
                }
              }}
              style={{
                cursor: isSelected ? "pointer" : "not-allowed",
                color: isSelected ? "#1890ff" : "inherit",
                textDecoration: isSelected ? "underline" : "none",
                fontWeight: isSelected ? "bold" : "normal",
              }}
            >
              {text}
            </span>
          );
        },
      },
      {
        title: "Distance",
        dataIndex: "distance",
        align: "center",
        width: 100,
      },
      {
        title: "Vehicle",
        dataIndex: "vehicle",
        align: "center",
        width: 100,
      },
      {
        title: "Body Type",
        dataIndex: "bodyType",
        align: "center",
        width: 100,
      },
      {
        title: "Driver Name",
        dataIndex: "drivername",
        align: "center",
        width: 150,
      },
      {
        title: "Permit",
        dataIndex: "permit",
        align: "center",
        width: 100,
        render: (permit: string) => {
          return permit?.toLowerCase() === "yes" ? (
            <CheckCircleOutlined style={{ color: "#52c41a", fontSize: "18px" }} />
          ) : (
            <CloseCircleOutlined style={{ color: "#ff4d4f", fontSize: "18px" }} />
          );
        }
      },
      {
        title: "Location",
        dataIndex: "location",
        align: "center",
        width: 150,
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text} placement="topLeft">
            <span>{text || "-"}</span>
          </Tooltip>
        ),
      },
    ];

    if (isWithinHomeWindow()) {
      columns.push({
        title: "Home",
        dataIndex: "startlocation",
        align: "center",
        width: 150,
        ellipsis: true,
        render: (text: string) => (
          <Tooltip title={text} placement="topLeft">
            <span>{text || "-"}</span>
          </Tooltip>
        ),
      });
    }

    columns.push(
      {
        title: "Total Trips",
        dataIndex: "trips",
        align: "center",
        width: 100,
      },
      {
        title: "Total Amount",
        dataIndex: "amount",
        align: "center",
        width: 120,
      },
      {
        title: "Minutes",
        dataIndex: "lastcompletedtime",
        align: "center",
        width: 100,
        render: (lastcompletedtime: string) => getMinutesSinceCompletion(lastcompletedtime),
      }
    );

    return columns;
  };

  return (
    <>
      <Button className="action-btn1" onClick={handleAssignClick} disabled={!selectedRowKey}>
        Assign
      </Button>

      <Modal
        title="Assign Vehicle"
        open={open}
        centered
        closable={false}
        confirmLoading={assignLoading}
        onOk={handleAssignConfirm}
        width={{ xs: "96vw", lg: "95vw", xxl: 1500 }}
        footer={
          <Row justify="center" style={{ gap: "16px" }}>
            <Button
              type="default"
              size="large"
              style={{ minWidth: 160 }}
              onClick={() => {
                setOpen(false);
                setSelectedVehicleRowKey(null);

                dispatch({
                  type: "RESET_SELECTED_CAB"
                });

                dispatch({
                  type: "GET_AVAILABLE_CABS_RADIUS_RESET"
                });
              }}            >
              Cancel
            </Button>
          </Row>
        }
        styles={{
          header: {
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            background: "#884299",
            color: "#ffffff",
            borderBottom: "none",
            padding: "16px 24px",
          },
          title: {
            color: "#ffffff",
            fontSize: 20,
            fontWeight: 700,
            letterSpacing: 0.5,
          },
          body: {
            paddingTop: 20,
          },
        }}
      >
        {contextHolder}
        <Card
          variant="borderless"
          style={{ background: "#cbb5e2", borderRadius: 12 }}
        >
          <Row gutter={[16, 16]}>
            <Col span={6}>
              <strong style={{ fontSize: 18 }}>Booking :</strong>
              <div>{selectedBooking.id || selectedBooking.BookId || "-"}</div>
            </Col>

            <Col span={6}>
              <strong style={{ fontSize: 18 }}>From :</strong>
              <div>{selectedBooking.fromloc || "-"}</div>
            </Col>

            <Col span={6}>
              <strong style={{ fontSize: 18 }}>To :</strong>
              <div>{selectedBooking.toloc || "-"}</div>
            </Col>

            <Col span={6}>
              <strong style={{ fontSize: 18 }}>Remarks :</strong>
              <div>{selectedBooking.remarks || "-"}</div>
            </Col>
          </Row>
        </Card>

        <Card
          style={{
            marginTop: 16,
            borderRadius: 12,
            backgroundColor: "#bde0fe34",
          }}
        >
          <Spin spinning={!!assignLoading} description="Assigning trip...">
            <Row
              justify="space-between"
              align="middle"
              style={{ marginBottom: 12 }}
            >
              <Col>
                <h3 style={{ margin: 0, fontSize: 18 }}>Available Vehicles</h3>
              </Col>

              <Col>
                <Search
                  placeholder="Search by Cab ID / Driver / Vehicle"
                  allowClear
                  enterButton={<SearchOutlined />}
                  style={{ width: "100%", maxWidth: 300, minWidth: 180 }}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  onSearch={(value) => setSearchText(value)}
                />
              </Col>
            </Row>

            <Row gutter={24}>
              <Col xs={24} lg={19}>
                <AssignVehicleTable
                  loading={availableCabsRadiusLoading}
                  rowSelection={{
                    type: "radio",
                    selectedRowKeys: selectedVehicleRowKey ? [selectedVehicleRowKey] : [],
                    onChange: (keys) => setSelectedVehicleRowKey(keys[0] as string),
                  }}
                  columns={getAssignColumns(selectedVehicleRowKey, handleAssignConfirm, messageApi)}
                  dataSource={filteredData}
                  pagination={{ pageSize: 5 }}
                  size="small"
                  bordered
                  scroll={{ x: 1300, y: 300 }}
                />
              </Col>

              <Col xs={24} lg={5}>
                <div
                  style={{
                    // auto so the card still sizes correctly once this Col
                    // stacks full-width below lg (ReassignModel already does this)
                    height: "auto",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    background: "#fff",
                    borderRadius: 12,
                    padding: 16,
                    border: "1px solid #d9d9d9",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
                  }}
                >
                  <div style={{
                    width: "100%",
                    textAlign: "center",
                    marginBottom: 16,
                    paddingBottom: 8,
                    borderBottom: "1px solid #f0f0f0"
                  }}>
                    <strong style={{ fontSize: 16, color: "#ffffffff", display: "block", backgroundColor: "#884299", padding: 10, borderRadius: 12 }}>
                      {selectedVehicle ? selectedVehicle.vehicle : "Select a Vehicle"}
                    </strong>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "space-between",
                      height: "100%",
                      width: "100%",
                      minHeight: 200,
                      background: "#f9f9f9",
                      borderRadius: 8,
                      overflow: "hidden"
                    }}
                  >
                    <div style={{ width: "100%" }}>
                      {selectedVehicle && (
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", padding: "0 20px", marginTop: 4 }}>
                          <div style={{ fontSize: 14, color: "#884299", fontWeight: "bold", textAlign: "left" }}>
                            {selectedVehicle.length && (
                              <div>Length: <span style={{ color: "#000" }}>{selectedVehicle.length}</span></div>
                            )}
                            {selectedVehicle.width && (
                              <div>Width: <span style={{ color: "#000" }}>{selectedVehicle.width}</span></div>
                            )}
                            {selectedVehicle.height && (
                              <div>Height: <span style={{ color: "#000" }}>{selectedVehicle.height}</span></div>
                            )}
                          </div>
                          {/* <div style={{ fontSize: 14, color: "#884299", fontWeight: "bold", textAlign: "right", display: "flex", flexWrap: "wrap", alignItems: "center", maxWidth: "50%" }}>
                          {selectedVehicle.bodyType && (
                            <span style={{ color: "#000" }}>{selectedVehicle.bodyType}</span>
                          )}
                        </div> */}
                        </div>
                      )}
                    </div>
                    <div>
                      {selectedVehicle ? (
                        <img
                          src={vehicleImageMap[selectedVehicle.vehicle.toLowerCase()] || ace}
                          alt={selectedVehicle.vehicle}
                          style={{
                            maxWidth: "100%",
                            maxHeight: "180px",
                            objectFit: "contain",
                            filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.1))"
                          }}
                        />
                      ) : (
                        <div style={{ textAlign: "center", color: "#bfbfbf" }}>
                          <CarOutlined style={{ fontSize: 48, marginBottom: 16, opacity: 0.3 }} />
                          <div>Please select a vehicle from the table to see details</div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </Spin>
        </Card>

      </Modal>
    </>
  );
};

export default Assignmodel;
